import React, { FC, ReactNode } from 'react'

export interface ButtonProps {
  type?: 'primary' | 'secondary' | 'tertiary'
  size?: 'small' | 'default' | 'large'
  children?: ReactNode
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>
  className?: string
  disabled?: boolean
  as?: 'button' | 'a' // determine the element type
  href?: string
  stopPropagation?: boolean
  target?: string
}

const Button: FC<ButtonProps> = ({
  type = 'primary',
  size = 'default',
  children = '',
  onClick = undefined,
  className = '',
  disabled = false,
  stopPropagation = false,
  as = 'button',
  href = '',
  target = '_self'
}) => {
  const baseStyles =
    'inline-flex justify-center items-center gap-2 font-normal focus:outline-none focus-visible:ring ring-offset-0 ring-blue-500'

  const typeStyles = {
    primary:
      'text-gray-50 bg-primary-800 hover:bg-primary-900 active:bg-primary-450 disabled:bg-primary-300',
    secondary:
      'text-primary-500 bg-primary-50 hover:text-primary-700 hover:bg-primary-100 active:bg-primary-200 focus-visible:bg-primary-100 disabled:text-primary-300 disabled:bg-primary-100',
    tertiary:
      'text-gray-900 bg-white border border-gray-400 hover:text-gray-1000 hover:bg-gray-50 active:bg-gray-100 focus-visible:text-gray-800 disabled:text-gray-500'
  }

  const sizeStyles = {
    small: 'h-9 px-4 py-[6px] text-sm rounded-lg',
    default: 'h-11 px-6 py-2 text-base rounded-xl',
    large: 'h-[54px] px-8 py-[10px] text-lg rounded-xl'
  }

  const classes = `${baseStyles} ${typeStyles[type]} ${sizeStyles[size]} ${className}`

  const Component = as === 'a' ? 'a' : 'button'
  const anchorProps = as === 'a' ? { href, role: 'button', target } : {}

  return (
    <Component
      className={classes}
      onClick={(e) => {
        if (stopPropagation) {
          e.stopPropagation()
        }
        onClick?.(e)
      }}
      disabled={disabled && as === 'button'}
      {...anchorProps}
    >
      {children}
    </Component>
  )
}

export default Button
